import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, UserCog, Shield, Trash2, Power, Pencil } from "lucide-react";

const MAX_USERS = 5;

// Full list of permissions — matches backend AVAILABLE_PERMISSIONS.
// Used in both „Utilizator nou" and „Editează utilizator" dialogs.
const PERMISSION_OPTIONS = [
  { key: "dashboard", label: "Panou (Dashboard)", desc: "KPI-uri + clienți noi, contracte, PV-uri" },
  { key: "analiza", label: "Dashboard Analiză", desc: "Rapoarte avansate, trends, agent breakdown" },
  { key: "clients", label: "Clienți", desc: "Bază de date clienți (adaugă, editează, arhivează)" },
  { key: "contracts", label: "Contracte", desc: "Vizualizează / creează / anulează contracte și PV-uri" },
  { key: "fise", label: "Fișă de Magazie", desc: "Jurnalul oficial consum substanțe" },
  { key: "stocuri", label: "Stocuri", desc: "Inventar substanțe, cantități, alerte sub prag" },
  { key: "sales", label: "Vânzări (Kanban)", desc: "Pipeline lead-uri + interacțiuni comerciale" },
  { key: "reports", label: "Rapoarte agenți", desc: "Performanță echipă + drill-down" },
];

export default function UsersPage() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [newPermissions, setNewPermissions] = useState(["dashboard", "clients", "contracts"]);
  const [editUser, setEditUser] = useState(null); // user being edited

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/users");
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Auto-open from TopBar quick action (?new=1)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const nonAdminCount = users.filter((u) => u.role === "user").length;
  const canAdd = nonAdminCount < MAX_USERS;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, role: "user", permissions: newPermissions });
      toast.success("Utilizator adăugat");
      setForm({ name: "", email: "", password: "" });
      setNewPermissions(["dashboard", "clients", "contracts"]);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleNewPerm = (key) =>
    setNewPermissions((cur) => (cur.includes(key) ? cur.filter((p) => p !== key) : [...cur, key]));

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Cont dezactivat" : "Cont activat");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    if (!confirm(`Ștergi utilizatorul ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilizator șters");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Administrare</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Utilizatori</h1>
          <p className="text-muted-foreground mt-1">
            {nonAdminCount} / {MAX_USERS} utilizatori adăugați (fără admini)
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          disabled={!canAdd}
          className="btn-gold hover:btn-gold h-11 px-5 disabled:opacity-50"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Adaugă utilizator
        </Button>
      </header>

      {/* Admin quick-access: sync data between preview and production */}
      {current?.role === "admin" && (
        <a
          href="/admin/sync"
          className="block border-2 border-dashed border-gold rounded-md p-4 bg-gold-soft/40 hover:bg-gold-soft transition-colors"
          data-testid="admin-sync-link"
        >
          <p className="font-semibold text-sm text-slate-900">Sync date între medii (preview ↔ producție)</p>
          <p className="text-xs text-slate-600 mt-1">
            Copiază toți clienții / contractele / PV-urile / stocurile dintr-un mediu în altul — util după publish.
          </p>
        </a>
      )}

      {!canAdd && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-4 text-sm">
          Ai atins limita de {MAX_USERS} utilizatori. Șterge sau dezactivează unul existent pentru a adăuga altul.
        </div>
      )}

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="label-eyebrow text-foreground">Nume</TableHead>
              <TableHead className="label-eyebrow text-foreground">Email</TableHead>
              <TableHead className="label-eyebrow text-foreground">Rol</TableHead>
              <TableHead className="label-eyebrow text-foreground">Status</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden md:table-cell">Creat</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Se încarcă...</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-mono-crm text-sm">{u.email}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1 rounded-sm border ${u.role === "admin" ? "border-gold text-gold-deep" : "border-border text-muted-foreground"}`}>
                    {u.role === "admin" ? <Shield className="w-3 h-3" /> : <UserCog className="w-3 h-3" />}
                    {u.role === "admin" ? "Admin" : "User"}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`inline-block text-xs uppercase tracking-widest px-2 py-1 rounded-sm ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {u.active ? "Activ" : "Dezactivat"}
                  </span>
                </TableCell>
                <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("ro-RO") : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {u.role !== "admin" && (
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditUser(u)}
                        data-testid={`edit-user-${u.id}`}
                        title="Editează"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(u)}
                        data-testid={`toggle-user-${u.id}`}
                        title={u.active ? "Dezactivează" : "Activează"}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeUser(u)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        data-testid={`delete-user-${u.id}`}
                        title="Șterge"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Utilizator nou</DialogTitle>
            <DialogDescription>
              Adaugă un membru al echipei și bifează la ce secțiuni are acces.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-eyebrow">Nume complet *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11" required data-testid="new-user-name" />
            </div>
            <div>
              <Label className="label-eyebrow">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11" required data-testid="new-user-email" />
            </div>
            <div>
              <Label className="label-eyebrow">Parolă *</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-11" required minLength={6} data-testid="new-user-password" />
              <p className="text-xs text-muted-foreground mt-1">Minim 6 caractere. Comunică parola personal utilizatorului.</p>
            </div>

            <div>
              <Label className="label-eyebrow">Ce vede în aplicație (meniul lateral)</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Bifează secțiunile la care are acces. Poți modifica și mai târziu.
              </p>
              <div className="space-y-2">
                {PERMISSION_OPTIONS.map((p) => {
                  const active = newPermissions.includes(p.key);
                  return (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => toggleNewPerm(p.key)}
                      data-testid={`new-perm-${p.key}`}
                      className={`w-full text-left border rounded-md p-2.5 transition-colors ${
                        active ? "border-gold bg-gold-soft" : "border-border bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-sm flex items-center justify-center shrink-0 mt-0.5 border ${
                          active ? "bg-gold border-gold text-[hsl(var(--charcoal))]" : "border-border bg-white"
                        }`}>
                          {active && "✓"}
                        </div>
                        <div className="min-w-0">
                          <p className={`font-medium text-sm ${active ? "text-gold-deep" : ""}`}>{p.label}</p>
                          <p className="text-[11px] text-muted-foreground">{p.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button type="submit" disabled={saving} className="btn-gold hover:btn-gold" data-testid="save-user-btn">
                {saving ? "Se salvează..." : "Adaugă utilizator"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {editUser && (
        <EditUserDialog
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
    </div>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  // Use the same options list as the create dialog (defined at module top)
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    password: "",
  });
  const [permissions, setPermissions] = useState(user.permissions || []);
  const [saving, setSaving] = useState(false);

  const togglePerm = (key) =>
    setPermissions((cur) => (cur.includes(key) ? cur.filter((p) => p !== key) : [...cur, key]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, permissions };
      if (form.password && form.password.trim()) payload.password = form.password;
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Utilizator actualizat");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Editează utilizator</DialogTitle>
          <DialogDescription>
            Nume / email / parolă și ce anume vede în aplicație (permisiuni).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-eyebrow">Nume complet</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 h-11"
              required
              data-testid="edit-user-name"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 h-11"
              required
              data-testid="edit-user-email"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Parolă nouă (opțional)</Label>
            <Input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 h-11"
              placeholder="Lasă gol pentru a păstra cea actuală"
              minLength={form.password ? 6 : undefined}
              data-testid="edit-user-password"
            />
            <p className="text-xs text-muted-foreground mt-1">Dacă schimbi parola, minim 6 caractere.</p>
          </div>

          {/* Permissions */}
          <div>
            <Label className="label-eyebrow">Ce vede utilizatorul în aplicație</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Bifează secțiunile la care are acces din meniul lateral.
            </p>
            <div className="space-y-2">
              {PERMISSION_OPTIONS.map((p) => {
                const active = permissions.includes(p.key);
                return (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => togglePerm(p.key)}
                    data-testid={`perm-${p.key}`}
                    className={`w-full text-left border rounded-md p-3 transition-colors ${
                      active ? "border-gold bg-gold-soft" : "border-border bg-white hover:border-slate-400"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-sm flex items-center justify-center shrink-0 mt-0.5 border ${
                        active ? "bg-gold border-gold text-[hsl(var(--charcoal))]" : "border-border bg-white"
                      }`}>
                        {active && "✓"}
                      </div>
                      <div className="min-w-0">
                        <p className={`font-medium text-sm ${active ? "text-gold-deep" : ""}`}>{p.label}</p>
                        <p className="text-xs text-muted-foreground">{p.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Anulează</Button>
            <Button type="submit" disabled={saving} className="btn-gold hover:btn-gold" data-testid="save-edit-user-btn">
              {saving ? "Se salvează..." : "Salvează modificările"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
